import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import { User, UserRole } from "../types";
import { apiService } from "../services/apiService";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import {
  AUTH_STATE_INVALIDATED_EVENT,
  clearStoredAuthUser,
  getStoredAuthUser,
  isUnauthorizedAuthError,
  sanitizeAuthUser,
  storeAuthUser,
} from "../utils/authSession";

interface AuthContextType {
  user: User | null;
  companySettings: Record<string, any> | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  applyCompanySettings: (settings: Record<string, any>) => void;
  isAdmin: () => boolean;
  isManager: () => boolean;
  isProjectManager: () => boolean;
  isIntern: () => boolean;
  canManageWorkflows: () => boolean;
  isWorker: () => boolean;
  isEmployee: () => boolean;
  isAYCCompany: () => boolean;
  getWorkflowLabel: () => string;
  getWorkflowsLabel: () => string;
  getSubmitDescriptionLabel: (fallback?: string) => string;
  getSubmitDescriptionsLabel: (fallback?: string) => string;
  getSubmitDescriptionPlaceholder: (fallback?: string) => string;
  hasWorkflowTerminologyOverride: () => boolean;
  isPageHidden: (pageKey: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation([
    "auth",
    "workflows",
    "common",
    "activities",
    "projects",
  ]);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuthState = useCallback(() => {
    clearStoredAuthUser();
    setUser(null);
  }, []);

  const companySettings = useMemo(() => {
    const raw = user?.company?.settingsJson;
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as {
        terminology?: {
          workflowLabel?: string | Record<string, string>;
          workflowPlural?: string | Record<string, string>;
          submitDescriptionLabel?: string | Record<string, string>;
          submitDescriptionPlural?: string | Record<string, string>;
          submitDescriptionPlaceholder?: string | Record<string, string>;
        };
        navigation?: {
          hiddenPages?: Record<string, any> | string[];
        };
      } & Record<string, any>;
    } catch (error) {
      return null;
    }
  }, [user]);

  useEffect(() => {
    const handleAuthStateInvalidated = () => {
      clearAuthState();
    };

    const checkAuthStatus = async () => {
      try {
        // The server-side cookie is the source of truth for the session.
        const freshUser = sanitizeAuthUser(await apiService.getCurrentUser());
        storeAuthUser(freshUser);
        setUser(freshUser);
      } catch (error) {
        if (isUnauthorizedAuthError(error)) {
          clearAuthState();
        } else {
          const cachedUser = getStoredAuthUser<User>();
          if (cachedUser) {
            setUser(cachedUser);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    window.addEventListener(
      AUTH_STATE_INVALIDATED_EVENT,
      handleAuthStateInvalidated,
    );

    const currentPath = window.location.pathname;
    const isPublicAuthRoute =
      currentPath === "/login" || currentPath === "/forgot-password";
    const cachedUser = getStoredAuthUser<User>();

    if (isPublicAuthRoute && !cachedUser) {
      setIsLoading(false);
    } else {
      void checkAuthStatus();
    }

    return () => {
      window.removeEventListener(
        AUTH_STATE_INVALIDATED_EVENT,
        handleAuthStateInvalidated,
      );
    };
  }, [clearAuthState]);

  const login = async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.login({ username, password });
      const nextUser = sanitizeAuthUser(response.user);

      // No need to store token, it's in HTTP-only cookie
      storeAuthUser(nextUser);
      setUser(nextUser);

      toast.success(t("loginSuccess"));
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("loginFailed"));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuthState();
    // Clear any existing cookies manually
    document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    void apiService.logout().catch((error) => {
    });
    toast.success(t("logoutSuccess"));
  };

  const applyCompanySettings = (settings: Record<string, any>) => {
    setUser((currentUser) => {
      if (!currentUser?.company) {
        return currentUser;
      }

      const nextUser = {
        ...currentUser,
        company: {
          ...currentUser.company,
          settingsJson: JSON.stringify(settings || {}),
        },
      };

      storeAuthUser(nextUser);
      return nextUser;
    });
  };

  const isAdmin = (): boolean => {
    return user?.role === UserRole.ADMIN;
  };

  const isManager = (): boolean => {
    return user?.role === UserRole.MANAGER;
  };

  const isProjectManager = (): boolean => {
    return user?.role === UserRole.PROJECT_MANAGER;
  };

  const isIntern = (): boolean => {
    return user?.role === UserRole.INTERN;
  };

  const canManageWorkflows = (): boolean => {
    return isAdmin() || isManager() || isProjectManager();
  };

  const isWorker = (): boolean => {
    return user?.role === UserRole.EMPLOYEE || user?.role === UserRole.INTERN;
  };

  const isEmployee = (): boolean => {
    return user?.role === UserRole.EMPLOYEE || user?.role === UserRole.INTERN;
  };

  const isAYCCompany = (): boolean => {
    return user?.company?.name === "AYC";
  };

  const resolveLabel = (
    label?: string | Record<string, string>,
  ): string | null => {
    if (!label) {
      return null;
    }
    if (typeof label === "string") {
      return label.trim() || null;
    }

    const lang = i18n.language || "tr";
    if (label[lang]) {
      return label[lang].trim();
    }

    if (lang.includes("-")) {
      const base = lang.split("-")[0];
      if (label[base]) {
        return label[base].trim();
      }
    }

    if (label.tr) {
      return label.tr.trim();
    }

    if (label.en) {
      return label.en.trim();
    }

    const fallback = Object.values(label).find(
      (value) => value && value.trim(),
    );
    return fallback ? fallback.trim() : null;
  };

  const normalizeAycWorkflowLabel = (
    label: string,
    isPlural: boolean,
  ): string => {
    if (!isAYCCompany()) {
      return label;
    }

    const normalized = label.trim().toLocaleLowerCase("tr-TR");
    const isProjectTypeTerm =
      normalized === "proje türü" ||
      normalized === "proje turu" ||
      normalized === "proje türleri" ||
      normalized === "proje turleri";

    if (!isProjectTypeTerm) {
      return label;
    }

    return isPlural
      ? t("projects:projectTypePlural", "Project Types")
      : t("projects:projectType", "Project Type");
  };

  const getWorkflowLabel = (): string => {
    const label = resolveLabel(companySettings?.terminology?.workflowLabel);
    if (label) {
      return normalizeAycWorkflowLabel(label, false);
    }
    return t("workflows:workflow", "İş Akışı");
  };

  const getWorkflowsLabel = (): string => {
    const label = resolveLabel(companySettings?.terminology?.workflowPlural);
    if (label) {
      return normalizeAycWorkflowLabel(label, true);
    }
    return t("workflows:title", "İş Akışları");
  };

  const getSubmitDescriptionLabel = (
    fallback = t("common:description"),
  ): string => {
    const label = resolveLabel(
      companySettings?.terminology?.submitDescriptionLabel,
    );
    return label || fallback;
  };

  const getSubmitDescriptionsLabel = (
    fallback = "Gönderim Açıklamaları",
  ): string => {
    const label = resolveLabel(
      companySettings?.terminology?.submitDescriptionPlural,
    );
    return label || fallback;
  };

  const getSubmitDescriptionPlaceholder = (
    fallback = t("activities:submitDescriptionPlaceholder"),
  ): string => {
    const explicitPlaceholder = resolveLabel(
      companySettings?.terminology?.submitDescriptionPlaceholder,
    );
    if (explicitPlaceholder) {
      return explicitPlaceholder;
    }

    const customLabel = resolveLabel(
      companySettings?.terminology?.submitDescriptionLabel,
    );
    if (!customLabel) {
      return fallback;
    }

    const lang = (i18n.language || "tr").toLowerCase();
    if (lang.startsWith("en")) {
      return `Enter ${customLabel.toLowerCase()}...`;
    }

    return `${customLabel} girin...`;
  };

  const hasWorkflowTerminologyOverride = (): boolean => {
    return Boolean(
      companySettings?.terminology?.workflowLabel ||
        companySettings?.terminology?.workflowPlural,
    );
  };

  const isPageHidden = (pageKey: string): boolean => {
    const hiddenPages = companySettings?.navigation?.hiddenPages;
    if (!hiddenPages) {
      return false;
    }

    if (Array.isArray(hiddenPages)) {
      return hiddenPages.includes(pageKey);
    }

    const entry = hiddenPages[pageKey];
    if (!entry) {
      return false;
    }

    if (entry === true) {
      return true;
    }

    const role = user?.role;
    if (Array.isArray(entry)) {
      return role ? entry.includes(role) : false;
    }

    if (typeof entry === "object" && Array.isArray(entry.roles)) {
      return role ? entry.roles.includes(role) : false;
    }

    return false;
  };

  const value: AuthContextType = {
    user,
    companySettings,
    isLoading,
    login,
    logout,
    applyCompanySettings,
    isAdmin,
    isManager,
    isProjectManager,
    isIntern,
    canManageWorkflows,
    isWorker,
    isEmployee,
    isAYCCompany,
    getWorkflowLabel,
    getWorkflowsLabel,
    getSubmitDescriptionLabel,
    getSubmitDescriptionsLabel,
    getSubmitDescriptionPlaceholder,
    hasWorkflowTerminologyOverride,
    isPageHidden,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
